import updateNotifierStyles from './update-notifier.styles.css?inline';
import { LitElement, html, nothing, unsafeCSS } from 'lit';
import { customElement, state } from 'lit/decorators.js';

import '@shoelace-style/shoelace/dist/components/button/button.js';
import '@shoelace-style/shoelace/dist/components/icon/icon.js';

type ReloadHandler = () => void | Promise<void>;

/**
 * The hard-refresh keys for the browser in use. Safari on a Mac reloads past its cache with
 * Cmd + Option + R; Chrome, Edge and Firefox on a Mac use Cmd + Shift + R; everything else uses
 * Ctrl + Shift + R. Safari is told apart by its user agent, which every Chromium and Firefox build
 * also stamps "Safari" into, so those names are excluded first.
 */
export function hardRefreshKeys(
  nav: Pick<Navigator, 'platform' | 'userAgent'>
): string {
  const ua = nav.userAgent;
  const isMac = /Mac|iPhone|iPad|iPod/.test(nav.platform || ua);
  if (!isMac) return 'Ctrl + Shift + R';
  const isSafari =
    /Safari/.test(ua) && !/Chrome|Chromium|CriOS|Edg|Firefox|FxiOS/.test(ua);
  return isSafari ? 'Cmd + Option + R' : 'Cmd + Shift + R';
}

/**
 * Blocking "new version" gate. This app is online-only and the PWA exists purely
 * for version control, so a new deploy must be applied before the user continues -
 * there is no "Later"/dismiss. It renders a full-screen modal overlay with a single
 * "Reload to continue" action.
 */
@customElement('agwa-update-notifier')
export class AgwaUpdateNotifier extends LitElement {
  static styles = unsafeCSS(updateNotifierStyles);

  @state() private open = false;
  @state() private reloading = false;

  private onReload?: ReloadHandler;

  show(onReload: ReloadHandler) {
    this.onReload = onReload;
    this.reloading = false;
    this.open = true;
  }

  hide() {
    this.open = false;
    this.onReload = undefined;
  }

  private async handleReload() {
    if (!this.onReload || this.reloading) return;
    this.reloading = true;
    try {
      await this.onReload();
    } catch {
      this.reloading = false;
    }
  }

  render() {
    if (!this.open) return nothing;
    return html`
      <div
        class="overlay"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="agwa-update-title"
      >
        <div class="card">
          <sl-icon class="icon" name="arrow-clockwise"></sl-icon>
          <div class="title" id="agwa-update-title">New version available</div>
          <div class="body">
            A newer version of 4C has been released. Reload to continue.
          </div>
          <div class="actions">
            <sl-button
              size="medium"
              variant="primary"
              ?loading=${this.reloading}
              @click=${this.handleReload}
              >Reload to continue</sl-button
            >
          </div>
          <!--
            Seen on dev 2026-09-28 in Chrome with three tabs open: the new version could not take
            over while any tab kept its live-update connection open through the old service
            worker, and closing the others cleared it. So the hint says both, and names the key
            for the browser in use. Since U31 (@APP-UPDATE-001, 002) a tab stops its connection
            when this gate appears and the worker no longer carries it, so other tabs no longer
            hold the update back; the hint stays for a tab still running a build from before
            that, which does neither.
          -->
          <div class="hint">
            <span
              >If it does not reload on its own after a few seconds, close every
              other 4C tab and window, then press:</span
            >
            <kbd>${hardRefreshKeys(navigator)}</kbd>
          </div>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'agwa-update-notifier': AgwaUpdateNotifier;
  }
}

function ensureHost(): AgwaUpdateNotifier {
  let host = document.querySelector<AgwaUpdateNotifier>('agwa-update-notifier');
  if (!host) {
    host = document.createElement('agwa-update-notifier');
    document.body.appendChild(host);
  }
  return host;
}

export const UpdateNotifier = {
  show(onReload: ReloadHandler) {
    ensureHost().show(onReload);
  },
  hide() {
    document.querySelector<AgwaUpdateNotifier>('agwa-update-notifier')?.hide();
  },
};
